import {
  forwardRef,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { RemoveScroll } from 'react-remove-scroll'
import { Check, ChevronDown } from 'lucide-react'
import { Label } from '@edvi-tech/ui/components/ui/label'
import { cn } from '@edvi-tech/ui/lib/utils'
import { withRequiredMark } from './requiredMark'

function flattenOptions(options) {
  return options.flatMap((item) => {
    if (item.children !== undefined) {
      return item.children.map((child) => ({
        ...child,
        group: item.title,
      }))
    }
    return [{ ...item, group: null }]
  })
}

function optionGroups(options) {
  const result = []
  let current = null
  options.forEach((item) => {
    if (item.children !== undefined) {
      result.push({ title: item.title, items: item.children })
      current = null
    } else {
      if (!current) {
        current = { title: null, items: [] }
        result.push(current)
      }
      current.items.push(item)
    }
  })
  return result
}

function sameValue(a, b) {
  return String(a ?? '') === String(b ?? '')
}

function useDismiss(open, setOpen, rootRef, menuRef) {
  useEffect(() => {
    if (!open) return undefined
    const onDoc = (e) => {
      if (rootRef.current?.contains(e.target)) return
      if (menuRef.current?.contains(e.target)) return
      setOpen(false)
    }
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open, setOpen, rootRef, menuRef])
}

function useAnchoredMenu(open, triggerRef, menuRef) {
  const [style, setStyle] = useState({})

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return undefined

    const update = (event) => {
      if (
        event &&
        menuRef?.current &&
        (event.target === menuRef.current ||
          menuRef.current.contains(event.target))
      ) {
        return
      }
      const rect = triggerRef.current.getBoundingClientRect()
      const gap = 6
      const maxH = 280
      const spaceBelow = window.innerHeight - rect.bottom - gap
      const spaceAbove = rect.top - gap
      const openUp = spaceBelow < 180 && spaceAbove > spaceBelow
      const height = Math.min(maxH, Math.max(140, openUp ? spaceAbove : spaceBelow))
      setStyle({
        position: 'fixed',
        left: Math.max(8, rect.left),
        width: Math.max(rect.width, 160),
        maxHeight: height,
        zIndex: 1600,
        ...(openUp
          ? { top: 'auto', bottom: window.innerHeight - rect.top + gap }
          : { top: rect.bottom + gap, bottom: 'auto' }),
      })
    }

    update()
    window.addEventListener('resize', update)
    window.addEventListener('scroll', update, true)
    return () => {
      window.removeEventListener('resize', update)
      window.removeEventListener('scroll', update, true)
    }
  }, [open, triggerRef, menuRef])

  return style
}

function SelectMenuPortal({ open, menuRef, menuStyle, className, children, ...rest }) {
  useEffect(() => {
    if (!open) return undefined
    const el = menuRef.current
    if (!el) return undefined
    const onWheel = (e) => {
      if (!el.contains(e.target)) return
      e.stopPropagation()
      if (el.scrollHeight <= el.clientHeight) return
      e.preventDefault()
      el.scrollTop += e.deltaY
    }
    el.addEventListener('wheel', onWheel, { passive: false, capture: true })
    return () => el.removeEventListener('wheel', onWheel, { capture: true })
  }, [open, menuRef])

  if (!open) return null
  return createPortal(
    <RemoveScroll allowPinchZoom enabled>
      <div
        ref={menuRef}
        className={className}
        style={menuStyle}
        {...rest}
      >
        {children}
      </div>
    </RemoveScroll>,
    document.body,
  )
}

/**
 * Compatibility Select — same Controls.Select props API.
 * Custom menu for both single and multiple (no native <select>).
 */
const Select = forwardRef(function Select(props, ref) {
  const {
    name,
    label,
    value,
    error = null,
    onChange,
    options = [],
    style,
    className,
    children: _children,
    multiple,
    manualSelectedItems = [],
    renderValue,
    variant: _variant,
    labelStyle: _labelStyle,
    placeholder = 'Select…',
    size: _size,
    fullWidth = true,
    ...other
  } = props

  const autoId = useId()
  const selectId = name || autoId
  const selected = useMemo(() => {
    if (multiple) {
      return Array.isArray(value) ? value : []
    }
    return value == null ? '' : value
  }, [multiple, value])

  if (multiple) {
    return (
      <MultiSelect
        ref={ref}
        id={selectId}
        name={name}
        label={label}
        selected={selected}
        options={options}
        error={error}
        className={cn(fullWidth && 'w-full', className)}
        style={style}
        placeholder={placeholder}
        renderValue={renderValue}
        manualSelectedItems={manualSelectedItems}
        onChange={onChange}
        {...other}
      />
    )
  }

  return (
    <SingleSelect
      ref={ref}
      id={selectId}
      name={name}
      label={label}
      selected={selected}
      options={options}
      error={error}
      className={cn(fullWidth && 'w-full', className)}
      style={style}
      placeholder={placeholder}
      onChange={onChange}
      {...other}
    />
  )
})

const SingleSelect = forwardRef(function SingleSelect(
  {
    id,
    name,
    label,
    selected,
    options,
    error,
    className,
    style,
    placeholder,
    onChange,
    disabled,
    required: _required,
    ...other
  },
  ref,
) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef(null)
  const triggerRef = useRef(null)
  const menuRef = useRef(null)
  const menuStyle = useAnchoredMenu(open, triggerRef, menuRef)
  useDismiss(open, setOpen, rootRef, menuRef)

  const flat = useMemo(() => flattenOptions(options), [options])
  const groups = useMemo(() => optionGroups(options), [options])
  const selectedItem = flat.find((item) => sameValue(item.id, selected))
  const display =
    selectedItem?.title ??
    (selected === '' || selected == null ? placeholder : String(selected))
  const isPlaceholder = !selectedItem

  const emit = (next) => {
    onChange?.({
      target: { name, value: next },
    })
  }

  const setTriggerRefs = (node) => {
    triggerRef.current = node
    if (typeof ref === 'function') ref(node)
    else if (ref) ref.current = node
  }

  return (
    <div
      className={cn('edvi-control-field edvi-select', className)}
      style={style}
      ref={rootRef}
    >
      {label ? (
        <Label
          htmlFor={id}
          className="mb-1.5 block text-[13px] font-medium text-[#1c2434]"
        >
          {withRequiredMark(label)}
        </Label>
      ) : null}

      <input type="hidden" name={name} value={selected ?? ''} />

      <div className={cn('edvi-select', open && 'is-open')}>
        <button
          ref={setTriggerRefs}
          id={id}
          type="button"
          className="edvi-select__trigger"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-invalid={Boolean(error)}
          disabled={disabled}
          onClick={() => setOpen((v) => !v)}
          {...other}
        >
          <span
            className={cn(
              'edvi-select__value',
              isPlaceholder && 'edvi-select__placeholder',
            )}
          >
            {display}
          </span>
          <ChevronDown size={16} strokeWidth={2.25} aria-hidden />
        </button>

        <SelectMenuPortal
          open={open && !disabled}
          menuRef={menuRef}
          menuStyle={menuStyle}
          className="edvi-select__menu"
          role="listbox"
        >
          {groups.map((group, gi) => (
            <div
              key={group.title || `g-${gi}`}
              className="edvi-select__group"
            >
              {group.title ? (
                <p className="edvi-select__group-title">{group.title}</p>
              ) : null}
              {group.items.map((item) => {
                const checked = sameValue(item.id, selected)
                return (
                  <button
                    key={String(item.id)}
                    type="button"
                    role="option"
                    aria-selected={checked}
                    className={cn(
                      'edvi-select__option',
                      checked && 'is-selected',
                    )}
                    onClick={() => {
                      emit(item.id)
                      setOpen(false)
                    }}
                  >
                    <span className="edvi-select__option-label">
                      {item.title}
                    </span>
                    {checked ? (
                      <Check size={14} strokeWidth={2.5} aria-hidden />
                    ) : null}
                  </button>
                )
              })}
            </div>
          ))}
          {!flat.length ? (
            <p className="edvi-select__empty">No options</p>
          ) : null}
        </SelectMenuPortal>
      </div>

      {error ? (
        <p className="edvi-control-field__msg edvi-control-field__msg--error">
          {error}
        </p>
      ) : null}
    </div>
  )
})

const MultiSelect = forwardRef(function MultiSelect(
  {
    id,
    name,
    label,
    selected,
    options,
    error,
    className,
    style,
    placeholder,
    renderValue,
    onChange,
    disabled,
    ...other
  },
  ref,
) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef(null)
  const triggerRef = useRef(null)
  const menuRef = useRef(null)
  const menuStyle = useAnchoredMenu(open, triggerRef, menuRef)
  useDismiss(open, setOpen, rootRef, menuRef)

  const flat = useMemo(() => flattenOptions(options), [options])
  const groups = useMemo(() => optionGroups(options), [options])
  const titleById = useMemo(() => {
    const map = new Map()
    flat.forEach((item) => {
      map.set(String(item.id), item.title)
    })
    return map
  }, [flat])

  const isSelected = (idValue) =>
    selected.some((v) => sameValue(v, idValue))

  const emit = (next) => {
    onChange?.({
      target: { name, value: next },
    })
  }

  const toggle = (idValue) => {
    const next = isSelected(idValue)
      ? selected.filter((v) => !sameValue(v, idValue))
      : [...selected, idValue]
    emit(next)
  }

  const defaultRenderValue = (vals) => {
    if (!vals.length) {
      return <span className="edvi-multiselect__placeholder">{placeholder}</span>
    }
    return (
      <div className="edvi-multiselect__chips">
        {vals.map((v) => (
          <span key={v} className="edvi-multiselect__chip">
            {titleById.get(String(v)) || v}
          </span>
        ))}
      </div>
    )
  }

  const setTriggerRefs = (node) => {
    triggerRef.current = node
    if (typeof ref === 'function') ref(node)
    else if (ref) ref.current = node
  }

  return (
    <div
      className={cn('edvi-control-field', className)}
      style={style}
      ref={rootRef}
    >
      {label ? (
        <Label
          htmlFor={id}
          className="mb-1.5 block text-[13px] font-medium text-[#1c2434]"
        >
          {withRequiredMark(label)}
        </Label>
      ) : null}

      <div className={cn('edvi-multiselect', open && 'is-open')}>
        <button
          ref={setTriggerRefs}
          id={id}
          type="button"
          className="edvi-multiselect__trigger"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-invalid={Boolean(error)}
          disabled={disabled}
          onClick={() => setOpen((v) => !v)}
          {...other}
        >
          <div className="edvi-multiselect__value">
            {typeof renderValue === 'function' ? (
              selected.length ? (
                renderValue(selected)
              ) : (
                <span className="edvi-multiselect__placeholder">{placeholder}</span>
              )
            ) : (
              defaultRenderValue(selected)
            )}
          </div>
          <ChevronDown size={16} strokeWidth={2.25} aria-hidden />
        </button>

        <SelectMenuPortal
          open={open && !disabled}
          menuRef={menuRef}
          menuStyle={menuStyle}
          className="edvi-multiselect__menu"
          role="listbox"
          aria-multiselectable
        >
          {groups.map((group, gi) => (
            <div
              key={group.title || `g-${gi}`}
              className="edvi-multiselect__group"
            >
              {group.title ? (
                <p className="edvi-multiselect__group-title">{group.title}</p>
              ) : null}
              {group.items.map((item) => {
                const idStr = String(item.id)
                const checked = isSelected(item.id)
                return (
                  <button
                    key={idStr}
                    type="button"
                    role="option"
                    aria-selected={checked}
                    className={cn(
                      'edvi-multiselect__option',
                      checked && 'is-selected',
                    )}
                    onClick={() => toggle(item.id)}
                  >
                    <span
                      className={cn(
                        'edvi-multiselect__check',
                        checked && 'is-on',
                      )}
                    >
                      {checked ? <Check size={12} strokeWidth={3} /> : null}
                    </span>
                    <span>{item.title}</span>
                  </button>
                )
              })}
            </div>
          ))}
          {!flat.length ? (
            <p className="edvi-multiselect__empty">No options</p>
          ) : null}
        </SelectMenuPortal>
      </div>

      {error ? (
        <p className="edvi-control-field__msg edvi-control-field__msg--error">
          {error}
        </p>
      ) : null}
    </div>
  )
})

export default Select
